// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { InputAdornment } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import debounce from 'lodash/debounce';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import { ReactComponent as SearchIcon } from '../assets/SearchIcon.svg';

import SolyticsInput from './SolyticsInput';
import SolyticsIconButton from './SolyticsIconButton';
import SolyticsButton from './SolyticsButton';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';

const SolyticsSearchBar = (props) => {
  const {
    id,
    name,
    size,
    fullWidth,
    placeholder,
    value: controlledValue,
    onChange,
    onSearchBtn,
    searchButtonLabel = 'Search',
    iconPosition = 'start',
    ariaDescription,
    styleVariant,
  } = props;

  const generatedId = useId();
  const descriptionId = id ?? generatedId;

  const [value, setValue] = useState(controlledValue || '');

  useEffect(() => {
    if (controlledValue !== value) {
      setValue(controlledValue || '');
    }
  }, [controlledValue]);

  const onChangeRef = useRef(onChange);

  // Create debounced function in useEffect to avoid accessing refs during render
  const debouncedRef = useRef();
  useEffect(() => {
    debouncedRef.current = debounce((e) => {
      onChangeRef.current?.(e);
    }, 300);
    return () => {
      if (debouncedRef.current) debouncedRef.current.cancel();
    };
  }, []);

  const handleSearchClick = useCallback(() => {
    onSearchBtn?.(value);
  }, [value, onSearchBtn]);

  // Removed stray line that caused syntax and ref errors
  const handleClear = useCallback(() => {
    setValue('');
    const syntheticEvent = { target: { value: '', name } };
    if (debouncedRef.current) debouncedRef.current.cancel();
    onChangeRef.current?.(syntheticEvent);
  }, [name]);

  const handleChange = useCallback((e) => {
    setValue(e.target.value);
    if (debouncedRef.current) debouncedRef.current(e);
  }, []);

  const startAdornment =
    iconPosition === 'start' ? (
      <InputAdornment position="start">
        <SearchIcon fontSize="small" />
      </InputAdornment>
    ) : null;

  const endAdornment = (
    <SolyticsBox
      sx={{ mr: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      {value && (
        <SolyticsIconButton
          aria-label="Clear search"
          size="small"
          onClick={handleClear}
          disableRipple
        >
          <CloseIcon width={20} height={20} />
        </SolyticsIconButton>
      )}
      {iconPosition === 'end' && (
        <InputAdornment position="end">
          <SearchIcon />
        </InputAdornment>
      )}
      {onSearchBtn && (
        <InputAdornment position="end">
          <SolyticsButton
            onClick={handleSearchClick}
            size="small"
            variant="contained"
            disabled={!value}
          >
            {searchButtonLabel}
          </SolyticsButton>
        </InputAdornment>
      )}
    </SolyticsBox>
  );

  if (!name || !onChange) return null;

  return (
    <SolyticsBox role="search">
      {ariaDescription && (
        <SolyticsBox sx={visuallyHidden} id={descriptionId} component="div">
          <SolyticsTypography>{ariaDescription}</SolyticsTypography>
        </SolyticsBox>
      )}

      <SolyticsInput
        id={id}
        name={name}
        size={size}
        fullWidth={fullWidth}
        type="text"
        value={value}
        onChange={handleChange}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        placeholder={placeholder ?? 'Search...'}
        aria-describedby={ariaDescription ? descriptionId : undefined}
        styleVariant={styleVariant}
      />
    </SolyticsBox>
  );
};

export default SolyticsSearchBar;
